import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ApiService } from '@env-lib/api/api.service'
import { decode, LatLngTuple } from '@googlemaps/polyline-codec'
import {
    WagonTrackingGeofenceOperationType as OpType,
    WagonTrackingGeofence,
    WagonTrackingGeofenceFeatureCollection,
    WagonTrackingGeofenceOperation,
    WagonTrackingGeofenceWagons,
    WagonTrackingGetGeofencesResponse,
    WagonTrackingPrivateGeofenceRequestParams,
    WagonTrackingPublicGeofenceRequestParams,
} from '@wagon-tracking-lib/wagon-tracking.model'
import { Observable, ReplaySubject } from 'rxjs'
import {
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    startWith,
} from 'rxjs/operators'

/**
 * In the exchange between frontend and backend we need to reverse the coordinates
 * due to the fact that GeoJSON uses LonLat and Leaflet uses LatLon
 *
 * @see https://macwright.com/lonlat/
 *
 * @param coords
 */
const reverseCoords = (coords: LatLngTuple): LatLngTuple => [
    coords[1],
    coords[0],
]

@Injectable({
    providedIn: 'root',
})
export class WagonTrackingGeofenceService {
    constructor(
        private api: ApiService,
        private http: HttpClient
    ) {}

    private operationSubject$ =
        new ReplaySubject<WagonTrackingGeofenceOperation>(1)

    readonly operation$ = this.operationSubject$.asObservable()

    readonly isCreating$ = this.operation$.pipe(
        filter(
            ({ op }) =>
                ![
                    OpType.selectPublic,
                    OpType.setCustomName,
                    OpType.setShape,
                ].includes(op)
        ),
        map(({ op }) => op === OpType.create),
        distinctUntilChanged(),
        startWith(false),
        shareReplay(1)
    )

    readonly selectedPrivateGeofenceId$ = this.operation$.pipe(
        map(({ op, payload }) =>
            op === OpType.selectPrivate ? payload : undefined
        ),
        distinctUntilChanged(),
        startWith(undefined),
        shareReplay(1)
    )

    readonly selectedPublicGeofenceId$ = this.operation$.pipe(
        filter(({ op }) => op !== OpType.setCustomName),
        map(({ op, payload }) =>
            op === OpType.selectPublic ? payload : undefined
        ),
        distinctUntilChanged(),
        startWith(undefined),
        shareReplay(1)
    )

    readonly customName$ = this.operation$.pipe(
        map(({ op, payload }) =>
            op === OpType.setCustomName ? payload : undefined
        ),
        distinctUntilChanged(),
        startWith(undefined),
        shareReplay(1)
    )

    readonly drawnShape$ = this.operation$.pipe(
        map(({ op, payload }) =>
            op === OpType.setShape ? payload : undefined
        ),
        distinctUntilChanged(),
        startWith(undefined),
        shareReplay(1)
    )

    perform(op: OpType, payload?: string) {
        this.operationSubject$.next({
            op,
            payload,
        })
    }

    getGeofences() {
        return this.http.get<WagonTrackingGetGeofencesResponse>(
            `${this.api.virtuwall.backendUrl}/geofences`
        )
    }

    getGeofenceDetails(geofenceId: string) {
        return this.http.get<WagonTrackingGeofence>(
            `${this.api.virtuwall.backendUrl}/geofences/${geofenceId}`
        )
    }

    /**
     * Create a new private geofence and subscribe to it.
     *
     * @param payload
     */
    postGeofence(payload: WagonTrackingPrivateGeofenceRequestParams) {
        return this.http.post<WagonTrackingGeofence>(
            `${this.api.virtuwall.backendUrl}/geofences`,
            payload
        )
    }

    /**
     * Create a subscription to a public geofence.
     *
     * @param geofenceId
     * @param payload
     */
    postGeofenceSubscription(
        geofenceId: string,
        payload: WagonTrackingPublicGeofenceRequestParams
    ) {
        return this.http.post<WagonTrackingGeofence>(
            `${this.api.virtuwall.backendUrl}/geofences/${geofenceId}/subscribe`,
            payload
        )
    }

    deleteGeofence(geofenceId: string) {
        return this.http.delete(
            `${this.api.virtuwall.backendUrl}/geofences/${geofenceId}`
        )
    }

    deleteGeofenceSubscription(geofenceId: string) {
        return this.http.delete(
            `${this.api.virtuwall.backendUrl}/geofences/${geofenceId}/subscribe`
        )
    }

    makeGeofenceCollection(
        geofences: WagonTrackingGeofence[]
    ): WagonTrackingGeofenceFeatureCollection {
        return {
            type: 'FeatureCollection',
            features: geofences.map((geofence) => ({
                properties: {
                    geofence,
                },
                geometry: {
                    type: 'Polygon',
                    coordinates: [decode(geofence.shape).map(reverseCoords)],
                },
                type: 'Feature',
            })),
        }
    }

    getWagonsInGeofence(geofenceId: string): Observable<string[]> {
        return this.http
            .get<WagonTrackingGeofenceWagons>(
                `${this.api.connectEvent.backendUrl}/geofences/${geofenceId}/assets`
            )
            .pipe(map((response) => response.assetIds))
    }
}
